import { type EventContextActions } from '@/Contexts/App/EventContext';
import { isWorklistParamsEquivalent, isWorklistViewParamsEquivalent } from '@/Logic/WorklistEquivalenceLogic';
import { CustomWorklistSaveResult } from '@/Models/API/Worklist/CustomWorklistSaveResult';
import { isEntityLoaded, type EntityLoaded } from '@/Models/Entity';
import { type CustomWorklist } from '@/Models/GenericWorklist/CustomWorklist';
import { type Worklist } from '@/Models/GenericWorklist/WorklistParams';
import { type WorklistInitialData } from '@/Models/GenericWorklist/WorklistSideBarData';
import { UserModel } from '@/Models/User';
import { t } from 'i18next';
import * as API from '../../../API';
import GroupLevel from '../Components/GroupLevel';
import { ALL_WORKLIST_COLUMNS, ALL_WORKLIST_FILTER_PROPERTIES } from '../Logic/WorklistProperties';
import { type IWorklistStore } from './WorklistStore';
import { IWorklistsDashboardStore } from '@/Stores/WorklistsDashboard/WorklistsDashboardStore';

export interface ICustomWorklistHelper {
    readonly currentlySelectedCustomWorklist: CustomWorklist | null;
    readonly isActiveWorklistIdenticalToActiveCustomWorklist: boolean;
    readonly isCustomWorklistSelected: boolean;
    readonly userCanEditCurrentCustomWorklist: boolean;
    get savedCustomWorklists(): CustomWorklist[];
    get isDeleteCustomWorklistButtonAvailable(): boolean;
    deleteCurrentSelectedCustomWorklist: () => Promise<void>;
    deselectCustomWorklist: () => void;
    toggleSidebarCollapsedState(): Promise<void>;
}

export class CustomWorklistHelper implements ICustomWorklistHelper {
    private readonly eventContext: EventContextActions;
    private readonly user: UserModel;
    private readonly worklistsDashboardStore: IWorklistsDashboardStore;
    private readonly worklistStore: IWorklistStore;

    constructor(
        eventContext: EventContextActions,
        user: UserModel,
        worklistsDashboardStore: IWorklistsDashboardStore,
        worklistStore: IWorklistStore,
    ) {
        this.eventContext = eventContext;
        this.user = user;
        this.worklistsDashboardStore = worklistsDashboardStore;
        this.worklistStore = worklistStore;

        this.restoreSelectedCustomWorklist();
    }

    get currentlySelectedCustomWorklist(): CustomWorklist | null {
        const selectedID = this.worklistStore.selectedCustomWorklistID;
        if (selectedID === null) {
            return null;
        }

        return this.savedCustomWorklists.find((w) => w.ID === selectedID) ?? null;
    }

    get currentWorklistTitle(): string {
        return this.getWorklistTitle(this.currentlySelectedCustomWorklist);
    }

    get isActiveWorklistIdenticalToActiveCustomWorklist(): boolean {
        const activeCustomWorklist = this.currentlySelectedCustomWorklist?.Worklist;
        if (!activeCustomWorklist) {
            return false;
        }

        const activeWorklist = this.worklistStore.worklist;
        const paramsEquivalent = isWorklistParamsEquivalent(activeWorklist.Params, activeCustomWorklist.Params, true);
        const viewConfigsEquivalent = isWorklistViewParamsEquivalent(activeWorklist.View, activeCustomWorklist.View);

        const matches = paramsEquivalent && viewConfigsEquivalent;
        return matches;
    }

    get isCustomWorklistSelected(): boolean {
        return this.currentlySelectedCustomWorklist !== null;
    }

    get savedCustomWorklists(): CustomWorklist[] {
        const initialPageData = this.worklistStore.initialPageData;
        if (!isEntityLoaded(initialPageData)) {
            return [];
        }

        return initialPageData.CustomWorklists;
    }

    get userCanEditCurrentCustomWorklist(): boolean {
        if (this.currentlySelectedCustomWorklist?.GroupLevel === GroupLevel.Site && !this.user.admin) {
            return false;
        }

        return true;
    }

    activateCustomWorklistByID = (ID: number): void => {
        const matchingCustomWorklistsByID = this.savedCustomWorklists.find((worklist) => worklist.ID === ID);
        if (!matchingCustomWorklistsByID) {
            throw new Error(`No custom worklist found with ID ${ID}`);
        }

        this.activateCustomWorklist(matchingCustomWorklistsByID);
    };

    get isDeleteCustomWorklistButtonAvailable(): boolean {
        return !!this.currentlySelectedCustomWorklist
            && this.userCanEditCurrentCustomWorklist;
    }

    deleteCurrentSelectedCustomWorklist = async (): Promise<void> => {
        const currentWorklist = this.currentlySelectedCustomWorklist;

        if (!currentWorklist) {
            throw new Error('No custom worklist');
        }

        const ID = currentWorklist.ID;
        const success = await API.worklist.deleteFilter(ID);

        if (!success) {
            throw new Error('Failed to delete filter');
        }

        const newListOfSavedWorklists = this.savedCustomWorklists.filter((customWorklist) => customWorklist.ID !== ID);
        this.setSavedWorklists(newListOfSavedWorklists);

        this.deselectCustomWorklist();
        this.eventContext.emit('CustomWorklistsChanged', null);
        this.refreshWorklistsDashboardDataAfterCustomWorklistChange();
    };

    deselectCustomWorklist = (): void => {
        this.worklistStore.setSelectedCustomWorklistID(null);
        this.worklistStore.setTitle(this.getWorklistTitle(null));
        this.worklistStore.resetWorklistToDefault();
    };

    saveCurrentWorklistAsCustomWorklist = async (name: string, groupLevel: GroupLevel, groupID?: number, description?: string): Promise<void> => {
        const currentWorklist = this.worklistStore.worklist;
        const saveResult = await API.worklist.saveCustomWorklist(name, groupLevel, groupID, description, currentWorklist);

        this.updateCustomWorklistsFromSaveResult(saveResult);
    };

    updateCurrentCustomWorklist = async (name: string, groupLevel: GroupLevel, groupID?: number, description?: string): Promise<void> => {
        const currentWorklist = this.worklistStore.worklist;
        const currentSelectedCustomWorklist = this.currentlySelectedCustomWorklist;

        if (!currentSelectedCustomWorklist) {
            throw new Error(`Cant update a custom worklist if no custom worklist is selected`);
        }

        const updatedCustomWorklist: CustomWorklist = {
            ...currentSelectedCustomWorklist,
            Description: description,
            GroupLevel: groupLevel,
            GroupID: groupID,
            Name: name,
            Worklist: currentWorklist,
        };
        const saveResult = await API.worklist.updateCustomWorklist(updatedCustomWorklist);

        this.updateCustomWorklistsFromSaveResult(saveResult);
        this.refreshWorklistsDashboardDataAfterCustomWorklistChange();
    };

    toggleSidebarCollapsedState = async (): Promise<void> => {
        const currentSelectedCustomWorklist = this.currentlySelectedCustomWorklist;

        if (!currentSelectedCustomWorklist) {
            return;
        }

        this.worklistStore.setSidebarCollapsed(currentSelectedCustomWorklist, !(currentSelectedCustomWorklist.UserConfig?.IsSidebarCollapsed ?? false));

        return API.worklist.updateCustomWorklistCollapsed(currentSelectedCustomWorklist.ID, (currentSelectedCustomWorklist.UserConfig?.IsSidebarCollapsed ?? false))
            .catch(() => {
                this.worklistStore.setSidebarCollapsed(currentSelectedCustomWorklist, !(currentSelectedCustomWorklist.UserConfig?.IsSidebarCollapsed ?? false));
                throw new Error('Failed to toggle sidebar collapse state');
            });
    };

    private readonly updateCustomWorklistsFromSaveResult = (saveResult: CustomWorklistSaveResult): void => {
        const { SavedWorklistID: id, Worklists: savedWorklists } = saveResult;

        this.setSavedWorklists(savedWorklists);
        this.activateCustomWorklistByID(id);

        this.eventContext.emit('CustomWorklistsChanged', null);
    };

    private readonly activateCustomWorklist = (customWorklist: CustomWorklist): void => {
        this.worklistStore.setSelectedCustomWorklistID(customWorklist.ID);
        this.worklistStore.setTitle(this.currentWorklistTitle);

        const worklist = this.generateWorklistFromSavedCustomWorklist(customWorklist);
        this.worklistStore.setWorklist(worklist);
    };

    private readonly generateWorklistFromSavedCustomWorklist = (customWorklist: CustomWorklist): Worklist => {
        const savedColumns = customWorklist.Worklist.View.Columns;
        const savedFilters = customWorklist.Worklist.View.Filters;

        const savedColumnIds = new Set(savedColumns.map((col) => col.ID));
        const missingColumns = ALL_WORKLIST_COLUMNS
            .filter((columnId) => !savedColumnIds.has(columnId))
            .map((columnId) => ({ ID: columnId, Visible: false }));
        const columns = [...savedColumns, ...missingColumns];

        const savedFilterIds = new Set(savedFilters.map((filter) => filter.ID));
        const missingFilters = ALL_WORKLIST_FILTER_PROPERTIES
            .filter((filterId) => !savedFilterIds.has(filterId))
            .map((filterId) => ({ ID: filterId, Visible: false }));
        const filters = [...savedFilters, ...missingFilters];

        return {
            Params: {
                ...customWorklist.Worklist.Params,
                Pagination: {
                    ...customWorklist.Worklist.Params.Pagination,
                    Offset: 0,
                },
            },
            View: {
                Columns: [...columns],
                Filters: [...filters],
            },
        };
    };

    private readonly setSavedWorklists = (savedFilters: CustomWorklist[]): void => {
        const currentPageData = this.worklistStore.initialPageData;
        if (!isEntityLoaded(currentPageData)) {
            throw new Error('Trying to update saved filters while pageData is still loading');
        }
        const pageData: EntityLoaded<WorklistInitialData> = {
            ...currentPageData,
            CustomWorklists: savedFilters,
        };

        this.worklistStore.setInitialPageData(pageData);
    };

    private readonly restoreSelectedCustomWorklist = (): void => {
        const selectedID = this.worklistStore.selectedCustomWorklistID;
        if (selectedID === null || this.savedCustomWorklists.length === 0) {
            return;
        }

        const customWorklist = this.savedCustomWorklists.find((w) => w.ID === selectedID);
        if (customWorklist) {
            this.worklistStore.setTitle(this.getWorklistTitle(customWorklist));
        }
    };

    private readonly getWorklistTitle = (customWorklist: CustomWorklist | null): string => {
        const nonCustomWorklistName = t('Worklist');
        if (!customWorklist) {
            return nonCustomWorklistName;
        }
        return `${nonCustomWorklistName} - ${customWorklist.Name}`;
    };

    private readonly refreshWorklistsDashboardDataAfterCustomWorklistChange = (): void => {
        void this.worklistsDashboardStore.refresh();
    };
}
